import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'

import { hostListsEqual, hostsToText, MAX_BLOCKED_URLS, parseBlockedUrls } from './blockedUrlsUtils'

import styles from './BlockedUrlsModal.module.scss'

import Button from '@/components/Button'
import ConfirmDialog from '@/components/ConfirmDialog'
import Dialog from '@/components/Dialog'
import TextArea from '@/components/TextArea'
import { useToast } from '@/components/Toast/ToastContext'
import Tooltip from '@/components/Tooltip'
import { AppDispatch } from '@/store'
import { updateBrowserUrlDenyListThunk } from '@/store/features/user/userSlice'
import { SettingType } from '@/types'

interface BlockedUrlsModalProps {
  isOpen: boolean
  close: () => void
  currentList: string[]
  isAdmin: boolean
  authoringEnabled?: boolean
}

type Phase = 'editing' | 'saving'

const BlockedUrlsModal = ({ isOpen, close, currentList, isAdmin, authoringEnabled = true }: BlockedUrlsModalProps) => {
  const dispatch = useDispatch<AppDispatch>()
  const { updateToast } = useToast()

  const [text, setText] = useState(() => hostsToText(currentList))
  const [phase, setPhase] = useState<Phase>('editing')
  const [apiError, setApiError] = useState<string | null>(null)
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false)
  const wasOpenRef = useRef(false)

  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current
    wasOpenRef.current = isOpen
    if (!justOpened) return

    setText(hostsToText(currentList))
    setPhase('editing')
    setApiError(null)
    setShowDiscardConfirm(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const parsed = useMemo(() => parseBlockedUrls(text), [text])
  const isDirty = !hostListsEqual(parsed.hosts, currentList)

  const validationError = (() => {
    if (parsed.invalid.length > 0) {
      return `These don't look like valid URLs: ${parsed.invalid.slice(0, 5).join(', ')}`
    }
    if (parsed.hosts.length > MAX_BLOCKED_URLS) {
      return `Enter at most ${MAX_BLOCKED_URLS} URLs (you have ${parsed.hosts.length}).`
    }
    return null
  })()

  const saveBlockedReason = (() => {
    if (!isAdmin) return 'Only admins can edit blocked URLs.'
    if (isDirty && !authoringEnabled) return 'Adding and editing URLs is not enabled for this organization.'
    if (validationError) return validationError
    if (!isDirty) return 'No changes to save.'
    return null
  })()

  const statusMessage = (() => {
    if (apiError) return null
    if (saveBlockedReason && saveBlockedReason !== 'No changes to save.') return saveBlockedReason
    return null
  })()

  const doSave = async () => {
    setPhase('saving')
    setApiError(null)
    try {
      await dispatch(
        updateBrowserUrlDenyListThunk({ settings: { [SettingType.browserUrlDenyList]: parsed.hosts } })
      ).unwrap()
      updateToast('Blocked URLs updated', 'success')
      close()
    } catch (error) {
      setApiError(typeof error === 'string' ? error : 'Failed to save. Please try again.')
      setPhase('editing')
    }
  }

  const handleClose = () => {
    if (phase === 'saving') return
    if (isDirty) {
      setShowDiscardConfirm(true)
      return
    }
    close()
  }

  const handleDiscard = () => {
    setShowDiscardConfirm(false)
    close()
  }

  const saveButton = (
    <Button
      title="Save"
      onClick={() => void doSave()}
      type="dark"
      isDisabled={saveBlockedReason !== null || phase === 'saving'}
      isLoading={phase === 'saving'}
    />
  )

  return (
    <>
      <Dialog
        isOpen={isOpen && !showDiscardConfirm}
        title="Edit blocked URLs"
        close={handleClose}
        minWidth="min(560px, calc(100vw - 64px))"
      >
        <div className={styles.modalBody}>
          <p className={styles.helpText}>
            Voice-to-text turns off in a browser when the open site matches a blocked URL. Enter one or more URLs,
            separated by commas or new lines. Matching is by site — <code>example.com</code> blocks{' '}
            <code>example.com</code> and all its subdomains (e.g. <code>app.example.com</code>).
          </p>

          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="blocked-urls-input">
              Blocked URLs
            </label>
            <TextArea
              id="blocked-urls-input"
              className={styles.textArea}
              value={text}
              onInput={setText}
              rows={5}
              placeholder="chatgpt.com, notion.so&#10;docs.google.com"
              isDisabled={phase === 'saving' || !isAdmin || !authoringEnabled}
            />
          </div>

          <div className={styles.preview}>
            <span className={styles.previewLabel}>
              {parsed.hosts.length > 0
                ? `Will block (${parsed.hosts.length} site${parsed.hosts.length === 1 ? '' : 's'})`
                : 'Will block'}
            </span>
            {parsed.hosts.length === 0 ? (
              <p className={styles.emptyState}>No URLs are blocked.</p>
            ) : (
              <ul className={styles.previewList}>
                {parsed.hosts.map((host) => (
                  <li key={host} className={styles.previewHost}>
                    {host}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className={styles.errorSlot}>
            {apiError && (
              <p className={styles.errorText} role="alert">
                {apiError}
              </p>
            )}
            {statusMessage && (
              <p className={styles.statusText} role="status">
                {statusMessage}
              </p>
            )}
          </div>

          <div className={styles.modalActions}>
            <Button title="Cancel" onClick={handleClose} type="light" isDisabled={phase === 'saving'} />
            {saveBlockedReason !== null ? (
              <Tooltip text={saveBlockedReason} placement="top" hideArrow>
                {saveButton}
              </Tooltip>
            ) : (
              saveButton
            )}
          </div>
        </div>
      </Dialog>

      <ConfirmDialog
        isOpen={showDiscardConfirm}
        title="Discard unsaved changes?"
        message="Your changes to blocked URLs haven't been saved. If you discard now, you'll lose them."
        confirmLabel="Discard"
        cancelLabel="Keep editing"
        confirmType="destructive"
        onConfirm={handleDiscard}
        onCancel={() => setShowDiscardConfirm(false)}
      />
    </>
  )
}

export default BlockedUrlsModal
